/**
 * AppHeader — Header unifié
 * Logo + 5 prioritaires (pills RBAC) + sélecteur familles + user menu
 * Classes CSS : hrl-nav-pill, hrl-family-btn, hrl-dropdown-item (theme.css)
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { ProfilEnum } from '@/shared/types/user';
import { FAMILIES_META, FAMILY_PATHS, canAccess } from './familyConfig';

// ── Items prioritaires ────────────────────────────────────────────────────────

const PRIORITY_ITEMS = [
  { path: '/vocal',               label: 'Saisie vocale',  icon: 'bi-mic-fill',   profils: null as string[] | null, vocal: true },
  { path: '/vocal2',              label: 'Vocal DPI',      icon: 'bi-window-stack', profils: null as string[] | null, vocal: true },
  { path: '/dpi',                 label: 'DPI',    icon: 'bi-activity',   profils: null as string[] | null, vocal: false },
  { path: '/admin/agents',        label: 'Agents', icon: 'bi-diagram-3',  profils: [ProfilEnum.ADMIN], vocal: false },
  { path: '/admin/design-system', label: 'Design', icon: 'bi-palette',    profils: [ProfilEnum.ADMIN], vocal: false },
];

// ── Composant ─────────────────────────────────────────────────────────────────

export function AppHeader() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const location         = useLocation();

  const visiblePriority = PRIORITY_ITEMS.filter(i => canAccess(i.profils, user?.profil));
  const visibleFamilies = FAMILIES_META.filter(f => canAccess(f.profils, user?.profil));

  const activeFamilyId = visibleFamilies.find(f =>
    FAMILY_PATHS[f.id]?.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
  )?.id ?? null;

  const activeFamily = visibleFamilies.find(f => f.id === activeFamilyId);

  function isActivePriority(path: string): boolean {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }

  return (
    <nav className="navbar navbar-glass sticky-top" style={{ zIndex: 1000 }}>
      <div className="container-fluid px-3" style={{ gap: '6px', flexWrap: 'nowrap' }}>

        {/* ── Logo ─────────────────────────────────────────────────── */}
        <Link
          to="/home"
          className="text-decoration-none flex-shrink-0 me-1"
          style={{ fontSize: '1.2rem', fontWeight: 700, letterSpacing: '1px', lineHeight: 1 }}
        >
          <span style={{ color: 'var(--gray-400)' }}>HORAL</span>
          <span style={{ color: 'var(--purple-primary)' }}>IA</span>
        </Link>

        <div className="hrl-vr" />

        {/* ── Items prioritaires (pills) ───────────────────────────── */}
        {visiblePriority.map(item => {
          const isActive = isActivePriority(item.path);
          // VOCAL : toujours stylée comme active (accentuée)
          const isVocal  = item.vocal;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`hrl-nav-pill flex-shrink-0
                ${isVocal ? 'hrl-nav-pill--vocal' : ''}
                ${!isVocal && isActive ? 'active' : ''}`}
            >
              <i className={`bi ${item.icon}`} />
              <span className="d-none d-sm-inline">{item.label}</span>
            </Link>
          );
        })}

        {visibleFamilies.length > 0 && <div className="hrl-vr" />}

        {/* ── Sélecteur familles ───────────────────────────────────── */}
        {visibleFamilies.length > 0 && (
          <div className="dropdown flex-shrink-0">
            <button
              className={`hrl-family-btn dropdown-toggle ${activeFamilyId ? 'has-active' : ''}`}
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {activeFamily ? (
                <>
                  <i className={`bi ${activeFamily.icon}`} />
                  <span className="d-none d-md-inline">{activeFamily.label}</span>
                </>
              ) : (
                <>
                  <i className="bi bi-grid-3x3-gap" />
                  <span className="d-none d-md-inline">Pages</span>
                </>
              )}
            </button>
            <ul
              className="dropdown-menu dropdown-menu-end border-0 shadow"
              style={{ minWidth: '210px', borderRadius: '12px', padding: '6px', marginTop: '4px' }}
            >
              {visibleFamilies.map(family => {
                const isActive = family.id === activeFamilyId;
                return (
                  <li key={family.id}>
                    <button
                      className={`hrl-dropdown-item ${isActive ? 'is-active' : ''}`}
                      onClick={() => navigate(family.landing)}
                    >
                      <i className={`bi ${family.icon} hrl-family-icon`} />
                      <span style={{ flex: 1 }}>{family.label}</span>
                      {isActive && (
                        <i className="bi bi-check-lg" style={{ color: 'var(--purple-primary)', fontSize: '0.85rem' }} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* ── Spacer ───────────────────────────────────────────────── */}
        <div style={{ flex: 1 }} />

        {/* ── User info + logout ───────────────────────────────────── */}
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          {user && (
            <div className="d-none d-lg-block text-end" style={{ lineHeight: 1.3 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-700)' }}>
                {user.prenom} {user.nom}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>{user.profil}</div>
            </div>
          )}
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 flex-shrink-0"
            style={{ fontSize: '0.82rem', padding: '4px 10px', borderRadius: '20px' }}
          >
            <i className="bi bi-box-arrow-right" />
            <span className="d-none d-sm-inline">Déconnexion</span>
          </button>
        </div>

      </div>
    </nav>
  );
}
